import { useEffect, useMemo, useState } from "react";
import {
  Timestamp,
  onSnapshot,
  orderBy,
  query,
  where,
} from "firebase/firestore";
import { getDb } from "@/lib/firebase/client";
import { paths, refCol } from "@/lib/db/paths";
import { transactionSchema, type Transaction } from "@/lib/schemas";
import { startOfThisWeek } from "@/lib/utils/dates";

const cache = new Map<string, Transaction[]>();

/**
 * Wszystkie transakcje typu "earn" w household od poniedziałku tego tygodnia.
 * Dla parent dashboardu: agregacja per fundedBy → kto ile pokrył.
 */
export function useHouseholdEarnsThisWeek(
  hid: string | undefined,
): { transactions: Transaction[]; loading: boolean } {
  const cached = hid ? cache.get(hid) : undefined;
  const [state, setState] = useState<{
    transactions: Transaction[];
    loading: boolean;
  }>({
    transactions: cached ?? [],
    loading: cached === undefined,
  });

  // Stable Timestamp dla query (raz na poniedziałek)
  const since = useMemo(
    () => Timestamp.fromDate(startOfThisWeek()),
    [],
  );

  useEffect(() => {
    if (!hid) return;
    const db = getDb();
    const q = query(
      refCol(db, paths.transactions(hid)),
      where("type", "==", "earn"),
      where("createdAt", ">=", since),
      orderBy("createdAt", "desc"),
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        const transactions = snap.docs.flatMap((d) => {
          const parsed = transactionSchema.safeParse({ id: d.id, ...d.data() });
          return parsed.success ? [parsed.data] : [];
        });
        cache.set(hid, transactions);
        setState({ transactions, loading: false });
      },
      () => setState({ transactions: [], loading: false }),
    );
    return () => unsub();
  }, [hid, since]);

  return state;
}
